/**
 * Star Reward Animation
 *
 * Renders 15 random stars that animate from the book center to the
 * achievements nav icon using sine-wave trajectories.
 *
 * Stars preload at the midpoint of a reading module so the animation
 * is ready to play seamlessly when the book closes.
 */

const STAR_URLS = [
  '/svg/components/YellowStar1.svg',
  '/svg/components/RedStar1.svg',
  '/svg/components/PinkStar1.svg',
  '/svg/components/GreenStar1.svg',
  '/svg/components/BlueStar1.svg',
];

const STAR_COUNT = 15;
const STAR_INTERVAL_MS = 200;
const STAR_TRAVEL_MS = 1500;
const STAR_START_SIZE_VW = 10; // % of viewport width (desktop/tablet)
const STAR_START_SIZE_MOBILE_VW = 30; // % of viewport width (phones)
const STAR_END_SIZE_VW = 1; // % of viewport width

/** Preloaded Image elements keyed by URL */
const preloadedImages = new Map();

/** Whether preloading has started */
let preloadStarted = false;

/** The 15 randomly picked star URLs for the current animation */
let pickedStars = [];

/**
 * Preload star images into memory.
 * Call this at the midpoint of the module so images are cached.
 */
export function preloadStarImages() {
  if (preloadStarted) return;
  preloadStarted = true;

  // Pick 15 random stars
  pickedStars = Array.from(
    { length: STAR_COUNT },
    () => STAR_URLS[Math.floor(Math.random() * STAR_URLS.length)]
  );

  // Preload each unique URL
  const uniqueUrls = [...new Set(pickedStars)];
  uniqueUrls.forEach((url) => {
    if (!preloadedImages.has(url)) {
      const img = new Image();
      img.src = url;
      preloadedImages.set(url, img);
    }
  });

  console.log('⭐ Star images preloaded:', uniqueUrls.length, 'unique,', STAR_COUNT, 'total');
}

/**
 * Reset preload state for next module.
 */
export function resetStarPreload() {
  preloadStarted = false;
  pickedStars = [];
}

/**
 * Play the star reward animation.
 * Stars fly from the book center to the achievements nav icon.
 *
 * @returns {Promise<void>} Resolves when all stars have finished animating.
 */
export function playStarReward() {
  return new Promise((resolve) => {
    // Ensure stars are preloaded
    if (!preloadStarted) preloadStarImages();

    // Play reward sound effect once at the start of the animation
    try {
      // Ensure iOS audio context is unlocked before attempting playback
      import('./audioUnlock.js').then(({ ensureAudioUnlocked }) => ensureAudioUnlocked()).catch(() => {});
      const rewardSound = new Audio('/audio/wow.mp3');
      rewardSound.volume = 1;
      rewardSound.play().catch(() => { /* ignore autoplay restrictions */ });
    } catch { /* ignore audio errors */ }

    // Pre-create bounce sound for when stars land (iOS needs early creation)
    let bounceSound;
    try {
      bounceSound = new Audio('/audio/bounce.mp3');
      bounceSound.volume = 0.6;
    } catch { /* ignore */ }

    // Shine sounds play on specific star landings (1,3,6,9,12,14)
    const SHINE_SOUNDS = ['/audio/shine.mp3', '/audio/shine1.mp3', '/audio/shine2.mp3', '/audio/shine3.mp3', '/audio/shine4.mp3'];
    const SHINE_ON_STARS = [1, 3, 6, 9, 12, 14]; // 1-based star numbers

    // Find origin: center of the story-content / flipbook area
    const storyContent =
      document.querySelector('.story-content') || document.querySelector('#flipBook');
    const originRect = storyContent
      ? storyContent.getBoundingClientRect()
      : { left: window.innerWidth / 2, top: window.innerHeight / 2, width: 0, height: 0 };

    const originX = originRect.left + originRect.width / 2;
    const originY = originRect.top + originRect.height / 2;

    // Find destination: achievements nav icon
    const achievementsBtn = document.querySelector('[data-route="achievements"]');
    const destRect = achievementsBtn
      ? achievementsBtn.getBoundingClientRect()
      : { left: window.innerWidth - 60, top: 30, width: 40, height: 40 };

    const destX = destRect.left + destRect.width / 2;
    const destY = destRect.top + destRect.height / 2;

    // Create a container for all stars (removed after animation)
    const container = document.createElement('div');
    Object.assign(container.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '10000',
    });
    document.body.appendChild(container);

    const vw = window.innerWidth;
    const isMobile = vw < 768;
    const startSize = vw * ((isMobile ? STAR_START_SIZE_MOBILE_VW : STAR_START_SIZE_VW) / 100);
    const endSize = vw * (STAR_END_SIZE_VW / 100);

    let completedCount = 0;

    // Start pulsating the achievements nav icon
    const pulseInterval = startAchievementPulse(achievementsBtn);

    pickedStars.forEach((starUrl, index) => {
      setTimeout(() => {
        animateStar(container, starUrl, originX, originY, destX, destY, startSize, endSize, () => {
          // Play shine sound on specific star landings (1,3,6,9,12,14)
          completedCount++;
          if (SHINE_ON_STARS.includes(completedCount)) {
            try {
              const shinePath = SHINE_SOUNDS[Math.floor(Math.random() * SHINE_SOUNDS.length)];
              const shine = new Audio(shinePath);
              shine.volume = 0.7;
              shine.play().catch(() => {});
            } catch { /* ignore */ }
          }
          if (completedCount >= STAR_COUNT) {
            // All stars done — clean up
            stopAchievementPulse(achievementsBtn, pulseInterval);
            container.remove();
            resolve();
          }
        });
      }, index * STAR_INTERVAL_MS);
    });
  });
}

/**
 * Start pulsating the achievements nav icon (scale 1 → 1.2 → 1, 300ms each).
 * @param {HTMLElement|null} el - The achievements button element
 * @returns {number|null} Interval ID
 */
function startAchievementPulse(el) {
  if (!el) return null;
  el.style.transition = 'transform 300ms ease-in-out';
  let big = false;
  const id = setInterval(() => {
    big = !big;
    el.style.transform = big ? 'scale(1.2)' : 'scale(1)';
  }, 300);
  return id;
}

/**
 * Stop pulsating and reset the achievements nav icon to normal size.
 * @param {HTMLElement|null} el
 * @param {number|null} intervalId
 */
function stopAchievementPulse(el, intervalId) {
  if (intervalId) clearInterval(intervalId);
  if (el) {
    el.style.transform = 'scale(1)';
    // Remove inline transition after a tick so it doesn't interfere with other animations
    setTimeout(() => {
      if (el) el.style.transition = '';
    }, 350);
  }
}

/**
 * Animate a single star from origin to destination using requestAnimationFrame.
 * Uses a sine wave for lateral wobble to create a unique trajectory.
 *
 * @param {HTMLElement} container - Parent container
 * @param {string} url - Star image URL
 * @param {number} ox - Origin X
 * @param {number} oy - Origin Y
 * @param {number} dx - Destination X
 * @param {number} dy - Destination Y
 * @param {number} startSize - Start width in px
 * @param {number} endSize - End width in px
 * @param {Function} onComplete - Callback when animation finishes
 */
function animateStar(container, url, ox, oy, dx, dy, startSize, endSize, onComplete) {
  const el = document.createElement('img');
  el.src = url;
  el.alt = '';
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, {
    position: 'absolute',
    width: startSize + 'px',
    height: startSize + 'px',
    objectFit: 'contain',
    pointerEvents: 'none',
    willChange: 'transform, opacity, width, height',
    left: '0',
    top: '0',
  });
  container.appendChild(el);

  // Random sine wave parameters for unique trajectory
  const amplitude = 30 + Math.random() * 60; // 30-90px lateral wobble
  const frequency = 1 + Math.random() * 3; // 1-4 full cycles
  const phaseOffset = Math.random() * Math.PI * 2; // random start phase
  const totalRotation = 360 + Math.random() * 360; // 360-720 degrees rotation

  // Direction perpendicular to the travel line (for sine wobble)
  const travelDx = dx - ox;
  const travelDy = dy - oy;
  const travelLen = Math.sqrt(travelDx * travelDx + travelDy * travelDy) || 1;
  // Perpendicular unit vector
  const perpX = -travelDy / travelLen;
  const perpY = travelDx / travelLen;

  const startTime = performance.now();

  function tick(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / STAR_TRAVEL_MS, 1); // 0 → 1

    // Ease-out cubic for smooth deceleration
    const eased = 1 - Math.pow(1 - t, 3);

    // Linear interpolation along the travel line
    const baseX = ox + travelDx * eased;
    const baseY = oy + travelDy * eased;

    // Sine wave wobble (fades out as star approaches destination)
    const wobbleFade = 1 - eased; // wobble decreases as star arrives
    const wobble = Math.sin(frequency * Math.PI * 2 * eased + phaseOffset) * amplitude * wobbleFade;

    const finalX = baseX + perpX * wobble;
    const finalY = baseY + perpY * wobble;

    // Size interpolation
    const currentSize = startSize + (endSize - startSize) * eased;

    // Opacity: full at start, 0 at end
    const opacity = 1 - eased;

    // Apply transform (faster than setting left/top)
    const rotation = totalRotation * eased;
    el.style.transform = `translate(${finalX - currentSize / 2}px, ${finalY - currentSize / 2}px) rotate(${rotation}deg)`;
    el.style.width = currentSize + 'px';
    el.style.height = currentSize + 'px';
    el.style.opacity = opacity;

    if (t < 1) {
      requestAnimationFrame(tick);
    } else {
      // Animation complete — remove element
      el.remove();
      onComplete();
    }
  }

  requestAnimationFrame(tick);
}
